import {PageHeader} from "../menu/PageHeader";
import {Badge, Button, Card, Col, Container, Row} from "react-bootstrap";
import React, {Fragment, useContext, useEffect, useState} from "react";
import {UsacmContext} from "../App";
import {getMyCourseList} from "../api/CourseApi";
import {getErrorMessageForField} from "../utils/formUtils";
import {CONF_LABEL_KEY_COURSE, CONF_LABEL_KEY_COURSE_PAGE} from "../constants";
import {getConfLabel, makePlural, titleCase} from "../utils/usacmUtils";
import {CourseView} from "./CourseView";
import {useLocation, useNavigate} from "react-router";
import "./MyCourseList.scss";

/**
 * The courses the user can study - the ones they have access to, plus the ones they teach.  A course bought at a
 * price which is waiting for an admin to approve it is listed too, but can't be opened until it is approved.
 * Picking one opens it in the CourseView - the open course is the courseid url param, so the dashboard (which shows
 * this list with showHeader=false) can link straight to it.  onLoaded(courseList) is called each time the list loads.
 */
export function MyCourseList({showHeader = true, onLoaded = null}) {
  const navigate = useNavigate();
  const {search} = useLocation();
  const viewingCourseId = new URLSearchParams(search).get('courseid');
  const context = useContext(UsacmContext);
  const [conf,] = context.conference;
  const [navChanged,] = context.navigationChanged;
  const courseLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE));
  const pageLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE_PAGE));
  const [courseList, setCourseList] = useState(null);
  const [errors, setErrors] = useState([]);

  function loadCourses() {
    getMyCourseList((status, data, newErrors) => {
      if (status === 200) {
        setCourseList(data);
        onLoaded?.(data);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
  }

  useEffect(() => {
    loadCourses();
  }, [navChanged]);

  function viewCourse(courseId) {
    navigate('/my-course-list?courseid=' + courseId);
  }

  function getAccessStr(course) {
    if (course.expires_at) {
      return `Access until ${new Date(course.expires_at).toLocaleDateString()}`;
    }
    if (!course.has_access && course.is_instructor) {
      return 'Instructor';
    }
    if (!course.has_access && course.pending_review) {
      return 'Your purchase is waiting to be approved';
    }
    return '';
  }

  if (showHeader && viewingCourseId) {
    return <CourseView courseId={viewingCourseId} onClose={() => navigate('/dashboard')}/>;
  }

  const content = (
    <Fragment>
      {courseList && courseList.length === 0 &&
        <Row>
          <Col className="text-center mb-3">
            You don't have access to any {makePlural(courseLabel).toLowerCase()} yet.
          </Col>
        </Row>
      }

      {(courseList || []).map(course =>
        <Card key={course.id} className="mb-3 my-course-card">
          <Card.Body className="d-flex gap-3">
            {course.image_url &&
              <img src={course.image_url} alt="" className="my-course-image"/>
            }
            <div className="flex-grow-1">
              <Card.Title>
                {course.title}
                {!course.can_view && course.pending_review &&
                  <Badge bg="warning" text="dark" className="ms-2 align-middle">Pending Review</Badge>
                }
              </Card.Title>
              {course.subtitle && <Card.Subtitle className="mb-2 text-muted">{course.subtitle}</Card.Subtitle>}
              {course.description &&
                <div className="my-course-description" dangerouslySetInnerHTML={{__html: course.description}}/>
              }
              <div className="d-flex align-items-center mt-2">
                <small className="text-muted flex-grow-1">
                  {course.page_count} {course.page_count === 1 ? pageLabel : makePlural(pageLabel)}
                  {getAccessStr(course) && <span> &middot; {getAccessStr(course)}</span>}
                </small>
                <Button type="button" onClick={() => viewCourse(course.id)} disabled={!course.page_count || !course.can_view}>
                  View {courseLabel}
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {getErrorMessageForField(errors, '') &&
        <Row>
          <Col className="text-center mb-3 usacm-error-message">
            {getErrorMessageForField(errors, '')}
          </Col>
        </Row>
      }

      {!showHeader && courseList &&
        <Row>
          <Col className="text-center mb-3">
            <Button onClick={() => navigate('/course-list')}>Find {makePlural(courseLabel)}</Button>
          </Col>
        </Row>
      }
    </Fragment>
  );

  if (!showHeader) {
    return <div className="mt-3">{content}</div>;
  }

  return (
    <div>
      <PageHeader pageTitle={'My ' + makePlural(courseLabel)}/>
      <Container fluid className="usacm-container-medium">
        {content}
      </Container>
    </div>
  );
}
